import React, { useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Button, Stack, Tooltip, Popover,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import { TableCellInput } from './TableCellInput';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import WorkIcon from '@mui/icons-material/Work';
import { CodeCell } from './CodeCell';
import { ImportResolutionBadge, InfoableButton, type ButtonInfo } from './ImportResolutionBadge';
import { RECON_TABLE_INFO } from './billReconcileTableInfo';
import type {
  Bill, BillLineItem, POLineDetail, Product, PurchaseOrder, Supplier, PoStatus, GstTreatment,
} from '../data/types';
import { PO_STATUSES } from '../data/types';
import { classifyImportLine } from '../utils/codeMatching';
import { BRAND } from '../theme/tokens';

const FI_ORANGE = '#F57C00';
const fmt = (n: number) => `$${n.toFixed(2)}`;

const poHdrSx = {
  fontSize: '0.58rem', fontWeight: 800, color: BRAND, textTransform: 'uppercase' as const,
  px: 0.8, py: 0.6, backgroundColor: '#f0ebff', whiteSpace: 'nowrap' as const,
};
const billHdrSx = {
  color: '#fff', fontWeight: 700, fontSize: '0.62rem', py: 0.8,
  whiteSpace: 'nowrap' as const, backgroundColor: '#3d3060',
};

const PO_STATUS_OPTIONS: Array<{ value: PoStatus; label: string }> = PO_STATUSES.map(s => ({
  value: s,
  label: s,
}));

// ── GST badge ────────────────────────────────────────────────────────────────

const GstBadge: React.FC<{ treatment: GstTreatment; isOverride?: boolean; onClick?: () => void }> = ({
  treatment, isOverride, onClick,
}) => {
  const chip = (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
      <Chip label={treatment === 'ex' ? 'Ex GST' : 'Inc GST'} size="small" onClick={onClick}
        sx={{ fontSize: '0.62rem', height: 18, fontWeight: 700, cursor: onClick ? 'pointer' : 'default',
          backgroundColor: treatment === 'ex' ? '#e8f5e9' : '#fff3e0', color: treatment === 'ex' ? '#2e7d32' : '#e65100',
          border: isOverride ? `1px solid ${treatment === 'ex' ? '#2e7d32' : '#e65100'}` : 'none',
          '&:hover': onClick ? { opacity: 0.8 } : {} }} />
      {isOverride && <Typography sx={{ fontSize: '0.58rem', color: '#999', fontStyle: 'italic' }}>↻</Typography>}
    </Box>
  );
  return onClick ? (
    <InfoableButton info={RECON_TABLE_INFO.gstToggle}>{chip}</InfoableButton>
  ) : chip;
};

// ── Inline editable number cell ──────────────────────────────────────────────

export interface InlineNumberEditProps {
  value: number;
  onSave: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  width?: number | string;
  align?: 'left' | 'center' | 'right';
  colorFn?: (v: number) => string;
  fontWeight?: number;
  fontSize?: string;
  tooltip?: string;
  info?: ButtonInfo;
  suffix?: React.ReactNode;
  disabled?: boolean;
}

export const InlineNumberEdit: React.FC<InlineNumberEditProps> = ({
  value, onSave, min = 0, max, width = 44, align = 'center',
  colorFn, fontWeight = 700, fontSize = '0.66rem', tooltip, info, suffix, disabled,
}) => {
  const textColor = colorFn ? colorFn(value) : '#555';

  const content = (
    <Stack
      direction="row"
      spacing={0.3}
      alignItems="center"
      justifyContent={align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start'}
    >
      <TableCellInput
        value={String(value)}
        numeric
        disabled={disabled}
        align={align}
        width={width}
        fontSize={fontSize}
        fontWeight={fontWeight}
        color={textColor}
        onCommit={(raw) => {
          const parsed = parseFloat(raw);
          if (isNaN(parsed)) return;
          const clamped = Math.max(min, max !== undefined ? Math.min(max, parsed) : parsed);
          if (clamped !== value) onSave(clamped);
        }}
      />
      {suffix}
    </Stack>
  );

  if (info) return <InfoableButton info={info}>{content}</InfoableButton>;
  return tooltip ? <Tooltip title={tooltip} arrow placement="top"><span>{content}</span></Tooltip> : content;
};

// ── Reconciliation table ─────────────────────────────────────────────────────

export type ReconRow = { billLine: BillLineItem | null; poLine: POLineDetail | null };

export interface BillReconciliationTableProps {
  billImported: boolean;
  hasPOItems: boolean;
  syncedPoLines: POLineDetail[] | null;
  linkedPo: PurchaseOrder | null;
  bill: Bill;
  supplier: Supplier | null;
  lineItems: BillLineItem[];
  poLinesForRecon: POLineDetail[];
  reconRows: ReconRow[];
  products: Product[];
  trackSupplierCodes: boolean;
  processed: boolean;
  totalInc: number;
  globalGst: GstTreatment;
  lineGstOverrides: Record<number, GstTreatment>;
  selectedIds: Set<number>;
  billMarkup: string;
  showSalesPrice: boolean;
  onGlobalGstChange: (v: GstTreatment) => void;
  onApplyGlobalToAll: () => void;
  onToggleLineGst: (lineId: number, current: GstTreatment) => void;
  onToggleSelect: (lineId: number) => void;
  onToggleSelectAll: () => void;
  onClearSelection: () => void;
  onDeleteSelected: () => void;
  onDeleteLine: (lineId: number) => void;
  onBillMarkupChange: (v: string) => void;
  onToggleSalesPrice: () => void;
  salesOf: (price: number) => number;
  onUpdatePoLineField: (
    inventoryCode: string,
    field: 'orderedQty' | 'actualQty' | 'receivedToDate' | 'unitPrice',
    value: number,
  ) => void;
  onUpdateBillLineQty: (lineId: number, qty: number) => void;
  onUpdatePoStatus: (poId: string, status: PoStatus) => void;
  onResolveBillLine: (lineId: number) => void;
  lookupSupplierPriceBookEntry: (supplierId: string, supplierCode: string) => import('../data/types').SupplierPriceBookEntry | undefined;
  lookupSupplierCode: (supplierId: string, supplierCode: string) => string | undefined;
}

export const BillReconciliationTable: React.FC<BillReconciliationTableProps> = ({
  billImported,
  hasPOItems,
  linkedPo,
  bill,
  supplier,
  lineItems,
  poLinesForRecon,
  reconRows,
  products,
  trackSupplierCodes,
  processed,
  totalInc,
  globalGst,
  lineGstOverrides,
  selectedIds,
  billMarkup,
  showSalesPrice,
  onGlobalGstChange,
  onApplyGlobalToAll,
  onToggleLineGst,
  onToggleSelect,
  onToggleSelectAll,
  onClearSelection,
  onDeleteSelected,
  onDeleteLine,
  onBillMarkupChange,
  onToggleSalesPrice,
  salesOf,
  onUpdatePoLineField,
  onUpdateBillLineQty,
  onUpdatePoStatus,
  onResolveBillLine,
  lookupSupplierPriceBookEntry,
  lookupSupplierCode,
}) => {
  const [descPopover, setDescPopover] = useState<{ anchor: HTMLElement; text: string } | null>(null);

  if (!billImported && !hasPOItems) return null;

  return (
    <Box>

      {/*
        v1.37 — removed the standalone "N items synced to PO" chip. The step
        strip already marks Sync as complete and the "Next:" hint states the
        next action, so the chip was redundant guidance noise.
      */}

      {/* Header bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" spacing={0.8} alignItems="center">
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#444' }}>
            {billImported
              ? `${lineItems.length} Line Item${lineItems.length !== 1 ? 's' : ''}`
              : `${poLinesForRecon.length} PO Item${poLinesForRecon.length !== 1 ? 's' : ''} — awaiting invoice`}
          </Typography>
          <InfoableButton info={RECON_TABLE_INFO.editableQty}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.8, py: 0.2,
              borderRadius: 1, backgroundColor: '#f5f0ff', border: `1px solid ${BRAND}33`,
              color: BRAND, cursor: 'help', fontSize: '0.58rem', fontWeight: 700 }}>
              <InfoOutlinedIcon sx={{ fontSize: 11 }} />
              Qty columns are editable
            </Box>
          </InfoableButton>
        </Stack>
        {billImported && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, p: 0.5, px: 1, borderRadius: 1.5, backgroundColor: '#f8f5ff', border: `1px solid ${BRAND}22` }}>
            <TuneIcon sx={{ fontSize: 12, color: BRAND }} />
            <ToggleButtonGroup value={globalGst} exclusive onChange={(_, v) => { if (v) onGlobalGstChange(v); }} size="small"
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, fontSize: '0.65rem', px: 1, py: 0.15 } }}>
              <ToggleButton value="ex" sx={{ '&.Mui-selected': { backgroundColor: '#e8f5e9', color: '#2e7d32' } }}>Ex</ToggleButton>
              <ToggleButton value="inc" sx={{ '&.Mui-selected': { backgroundColor: '#fff3e0', color: '#e65100' } }}>Inc</ToggleButton>
            </ToggleButtonGroup>
            {Object.keys(lineGstOverrides).length > 0 && (
              <Button size="small" onClick={onApplyGlobalToAll} sx={{ textTransform: 'none', fontSize: '0.6rem', color: '#888', minWidth: 0, p: 0 }}>Reset</Button>
            )}
          </Box>
        </Stack>
        )}
      </Box>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 0.8, px: 1.2, borderRadius: 1.5, backgroundColor: '#fce4ec', border: '1px solid #ef9a9a' }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#c62828' }}>{selectedIds.size} selected</Typography>
          <Button size="small" variant="contained" startIcon={<DeleteSweepIcon sx={{ fontSize: 12 }} />} onClick={onDeleteSelected}
            sx={{ textTransform: 'none', fontSize: '0.68rem', fontWeight: 700, backgroundColor: '#c62828', '&:hover': { backgroundColor: '#b71c1c' }, py: 0.2 }}>
            Delete
          </Button>
          <Button size="small" onClick={onClearSelection} sx={{ textTransform: 'none', fontSize: '0.65rem', color: '#888', minWidth: 0 }}>Clear</Button>
        </Box>
      )}

      {/* ── Action bar above table: markup + sales toggle (sync lives in footer) ── */}
      {(linkedPo && processed) || billImported ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1.5 }} className="no-print">
            <Popover
              open={Boolean(descPopover)}
              anchorEl={descPopover?.anchor}
              onClose={() => setDescPopover(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { borderRadius: 1.5, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', maxWidth: 340, px: 1.5, py: 1 } }}
            >
              <Typography sx={{ fontSize: '0.78rem', color: '#333', lineHeight: 1.55 }}>
                {descPopover?.text}
              </Typography>
            </Popover>
          {billImported && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: '#999', textTransform: 'uppercase', mb: 0.2, whiteSpace: 'nowrap' }}>
                  Bill Markup
                </Typography>
                <TextField
                  value={billMarkup}
                  onChange={e => onBillMarkupChange(e.target.value)}
                  size="small" variant="outlined"
                  InputProps={{ endAdornment: <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: FI_ORANGE }}>%</Typography> }}
                  sx={{ width: 84,
                    '& input': { fontSize: '1rem', fontWeight: 800, color: FI_ORANGE, py: 0.5, textAlign: 'right' },
                    '& .MuiOutlinedInput-root': { backgroundColor: '#fff8f0' } }}
                />
                <Typography sx={{ fontSize: '0.52rem', color: '#bbb', mt: 0.15, whiteSpace: 'nowrap' }}>
                  {supplier?.code} default: {supplier?.defaultMarkup}%
                </Typography>
              </Box>
              <InfoableButton info={RECON_TABLE_INFO.salesPrice}>
                <Button size="small" variant={showSalesPrice ? 'contained' : 'outlined'}
                  onClick={onToggleSalesPrice}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.62rem', px: 1.2, py: 0.5, whiteSpace: 'nowrap', mt: 1.5,
                    backgroundColor: showSalesPrice ? FI_ORANGE : 'transparent',
                    borderColor: FI_ORANGE, color: showSalesPrice ? '#fff' : FI_ORANGE,
                    '&:hover': { backgroundColor: showSalesPrice ? '#bf360c' : '#fff3e0', borderColor: FI_ORANGE } }}>
                  Sales $
                </Button>
              </InfoableButton>
            </Box>
          )}
        </Box>
      ) : null}

      {/* Unified reconciliation table — PO left | Bill right — same row per matched item */}
      <Table size="small" sx={{ mb: 2, tableLayout: 'fixed', width: '100%' }}>
        <colgroup>
          <col style={{ width: 20 }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: 30 }} />
          <col style={{ width: 44 }} />
          <col style={{ width: 40 }} />
          <col style={{ width: 62 }} />
          {showSalesPrice && <col style={{ width: 64 }} />}
          <col style={{ width: 6 }} />
          <col style={{ width: 26 }} />
          <col style={{ width: 20 }} />
          <col style={{ width: '14%' }} />
          <col />
          <col style={{ width: 42 }} />
          <col style={{ width: 62 }} />
          {showSalesPrice && <col style={{ width: 64 }} />}
          <col style={{ width: 44 }} />
          <col style={{ width: 44 }} />
          <col style={{ width: 64 }} />
          <col style={{ width: 26 }} />
        </colgroup>
        {/*
          FI-022 FR-20 — provenance is GROUP-LEVEL only, never per-row.
          The Bill line-items table body MUST NOT carry an `AI` chip,
          a `Manual` chip, a `Source` column, or any per-row
          sparkle. Provenance lives on:
            (a) the post-table 'AI parsed N items' provenance tag
            (b) the totals panel split (AI-parsed $X · Manual $Y)
            (c) the breadcrumb Mixed-provenance pill
          Adding a per-row chip would violate AC-G3 and the canonical
          rule from ai-styling-pattern.md §4 ('No per-row AI/Manual
          chips in tables.'). Do NOT add a Source column here.
          Audited clean v1.6.4 (P2.4) — zero per-row chips found.
        */}
        <TableHead>
          <TableRow>
            <TableCell
              colSpan={showSalesPrice ? 8 : 7}
              sx={{ backgroundColor: BRAND, color: '#fff', fontWeight: 800, fontSize: '0.62rem', textTransform: 'uppercase', py: 0.5, px: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{linkedPo ? `${linkedPo.id} — Purchase Order` : 'Purchase Order'}</span>
                {linkedPo && (() => {
                  const s = linkedPo.status;
                  const poStatusColor =
                    s === 'Finalised' || s === 'Bill received' ? '#a5d6a7' :
                    s === 'All received' ? '#c8e6c9' :
                    s === 'Partial received' ? '#ffe082' :
                    s === 'Approved' ? '#90caf9' :
                    s === 'Submitted' ? '#b0bec5' :
                    s === 'Cancelled' ? '#ef9a9a' : '#e0e0e0';
                  const poStatusText =
                    s === 'Partial received' ? '#5d4037' :
                    s === 'Finalised' || s === 'Bill received' || s === 'All received' ? '#1b5e20' :
                    s === 'Cancelled' ? '#b71c1c' : '#1a237e';
                  const currentLabel = PO_STATUS_OPTIONS.find(o => o.value === s)?.label ?? s;
                  return (
                    <InfoableButton info={RECON_TABLE_INFO.poStatus}>
                      <Select
                        value={s}
                        size="small"
                        variant="standard"
                        disableUnderline
                        onChange={e => onUpdatePoStatus(linkedPo.id, e.target.value as PoStatus)}
                        onClick={e => e.stopPropagation()}
                        renderValue={() => currentLabel}
                        sx={{
                          height: 18,
                          fontSize: '0.6rem',
                          fontWeight: 800,
                          backgroundColor: poStatusColor,
                          color: poStatusText,
                          borderRadius: 4,
                          px: 0.8,
                          '& .MuiSelect-select': {
                            py: 0.15, pr: '18px !important', pl: 0.5, minHeight: 'unset',
                            display: 'inline-flex', alignItems: 'center',
                          },
                          '& .MuiSelect-icon': { color: poStatusText, right: 2, fontSize: 14 },
                          '&:hover': { filter: 'brightness(0.95)' },
                        }}
                        MenuProps={{
                          PaperProps: { sx: { '& .MuiMenuItem-root': { fontSize: '0.75rem', py: 0.6 } } },
                        }}>
                        {PO_STATUS_OPTIONS.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </InfoableButton>
                  );
                })()}
              </Box>
            </TableCell>
            <TableCell sx={{ backgroundColor: '#6a3fc0', p: 0 }} />
            <TableCell
              colSpan={showSalesPrice ? 11 : 10}
              sx={{ backgroundColor: billImported ? '#3d3060' : '#7c6ea0', color: '#fff', fontWeight: 800, fontSize: '0.62rem', textTransform: 'uppercase', py: 0.5, px: 1 }}>
              {billImported ? (
                <InfoableButton info={RECON_TABLE_INFO.billHeader}>
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.6 }}>
                    <span>Bill {bill.billNo}</span>
                    <Box component="span" sx={{ textTransform: 'none', fontWeight: 600, opacity: 0.72, fontSize: '0.56rem', letterSpacing: 0 }}>
                      imported from supplier invoice
                    </Box>
                  </Box>
                </InfoableButton>
              ) : 'Import supplier invoice or add items'}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ ...poHdrSx }}>#</TableCell>
            <TableCell sx={{ ...poHdrSx }}>Code</TableCell>
            <TableCell sx={{ ...poHdrSx }}>Description</TableCell>
            <TableCell align="center" sx={{ ...poHdrSx }} title="Ordered quantity">Qty</TableCell>
            <TableCell align="center" sx={{ ...poHdrSx }} title="Received quantity">Received Qty</TableCell>
            <TableCell align="center" sx={{ ...poHdrSx }} title="Still to receive">Remaining</TableCell>
            <TableCell align="right" sx={{ ...poHdrSx }}>Purchase $</TableCell>
            {showSalesPrice && <TableCell align="right" sx={{ ...poHdrSx, color: '#e65100', backgroundColor: '#fff3e0' }}>Sales $</TableCell>}
            <TableCell sx={{ backgroundColor: '#6a3fc0', p: 0 }} />
            <TableCell sx={{ ...billHdrSx, py: 0.8 }}>
              <Checkbox size="small"
                checked={lineItems.length > 0 && selectedIds.size === lineItems.length}
                indeterminate={selectedIds.size > 0 && selectedIds.size < lineItems.length}
                onChange={onToggleSelectAll}
                sx={{ color: '#fff', p: 0, '&.Mui-checked': { color: '#fff' }, '&.MuiCheckbox-indeterminate': { color: '#fff' } }} />
            </TableCell>
            <TableCell sx={{ ...billHdrSx }}>#</TableCell>
            <TableCell sx={{ ...billHdrSx }}>Code</TableCell>
            <TableCell sx={{ ...billHdrSx }}>Description</TableCell>
            <TableCell align="center" sx={{ ...billHdrSx }}>Bill Qty</TableCell>
            <TableCell align="right" sx={{ ...billHdrSx }}>Price</TableCell>
            {showSalesPrice && <TableCell align="right" sx={{ ...billHdrSx, backgroundColor: '#c25900' }}>Sales $</TableCell>}
            <TableCell align="center" sx={{ ...billHdrSx }}>GST</TableCell>
            <TableCell align="right" sx={{ ...billHdrSx }}>GST $</TableCell>
            <TableCell align="right" sx={{ ...billHdrSx }}>Total</TableCell>
            <TableCell sx={{ ...billHdrSx }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {(linkedPo ? reconRows : lineItems.map(bl => ({ billLine: bl as BillLineItem | null, poLine: null as POLineDetail | null }))).map(({ billLine, poLine }, idx) => {
            const isPoOnly = !!poLine && !billLine;
            const eg = billLine ? (lineGstOverrides[billLine.id] ?? globalGst) : globalGst;
            const ov = billLine ? billLine.id in lineGstOverrides : false;
            const sel = billLine ? selectedIds.has(billLine.id) : false;
            const overBilled = poLine && billLine && billLine.qty > poLine.orderedQty && poLine.orderedQty > 0;
            const rowBg = sel ? '#fce4ec' : idx % 2 === 0 ? '#fff' : '#fafafa';
            const poBg = sel ? '#f5dcea' : idx % 2 === 0 ? '#f9f7ff' : '#f5f2ff';
            const billPlaceholderBg = isPoOnly ? '#fff8f0' : '#f8f6ff';
            const rowKey = billLine ? billLine.id : `po-only-${poLine?.inventoryCode}`;

            return (
              <TableRow key={rowKey} sx={{ backgroundColor: rowBg, '&:hover .del-btn': { opacity: 1 } }}>
                <TableCell sx={{ fontSize: '0.6rem', color: '#aaa', px: 0.8, py: 0.55, backgroundColor: poBg }}>{poLine ? idx + 1 : ''}</TableCell>
                <TableCell sx={{ fontSize: '0.62rem', fontWeight: 700, color: FI_ORANGE, fontFamily: 'monospace', px: 0.8, py: 0.55, backgroundColor: poBg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {poLine ? poLine.inventoryCode : <Box component="span" sx={{ color: '#ddd', fontSize: '0.6rem' }}>—</Box>}
                </TableCell>
                <TableCell
                  sx={{ fontSize: '0.62rem', color: '#555', px: 0.8, py: 0.55, backgroundColor: poBg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: poLine?.description ? 'pointer' : 'default', '&:hover': poLine?.description ? { color: '#6a3fc0', textDecoration: 'underline dotted' } : {} }}
                  onClick={(e) => poLine?.description && setDescPopover({ anchor: e.currentTarget, text: poLine.description })}
                >
                  {poLine?.description ?? ''}
                </TableCell>
                <TableCell align="center" sx={{ px: 0.4, py: 0.55, backgroundColor: poBg }}>
                  {poLine ? (
                    <InlineNumberEdit
                      value={poLine.orderedQty}
                      onSave={(v) => onUpdatePoLineField(poLine.inventoryCode, 'orderedQty', v)}
                      min={0}
                      width={36}
                      fontSize="0.62rem"
                      fontWeight={600}
                      colorFn={() => '#555'}
                      tooltip="Edit ordered qty"
                      info={RECON_TABLE_INFO.poOrderedQty}
                    />
                  ) : ''}
                </TableCell>
                <TableCell align="center" sx={{ px: 0.4, py: 0.55, backgroundColor: poBg }}>
                  {poLine ? (
                    <Stack direction="row" spacing={0.3} alignItems="center" justifyContent="center">
                      <InlineNumberEdit
                        value={poLine.receivedToDate}
                        onSave={(v) => onUpdatePoLineField(poLine.inventoryCode, 'receivedToDate', v)}
                        min={0}
                        width={44}
                        fontSize="0.62rem"
                        fontWeight={700}
                        colorFn={(v) => v >= poLine.orderedQty ? '#2e7d32' : v > 0 ? FI_ORANGE : '#bbb'}
                        suffix={
                          poLine.receivedToDate >= poLine.orderedQty
                            ? <Box component="span" sx={{ ml: 0.3 }}>✓</Box>
                            : <Box component="span" sx={{ color: '#aaa', fontWeight: 500, ml: 0.2 }}>/{poLine.orderedQty}</Box>
                        }
                        tooltip="Edit received qty"
                        info={RECON_TABLE_INFO.poReceivedQty}
                      />
                      {poLine.copiedToJob && (
                        <InfoableButton info={RECON_TABLE_INFO.copiedToJobPo}>
                          <WorkIcon sx={{ fontSize: 11, color: '#2e7d32' }} />
                        </InfoableButton>
                      )}
                    </Stack>
                  ) : ''}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.62rem', px: 0.4, py: 0.55, backgroundColor: poBg }}>
                  {poLine ? (() => {
                    const remaining = Math.max(0, poLine.orderedQty - poLine.receivedToDate);
                    return (
                      <Box component="span" sx={{
                        fontWeight: 700,
                        color: remaining === 0 ? '#2e7d32' : remaining === poLine.orderedQty ? '#bbb' : FI_ORANGE,
                      }}>
                        {remaining}
                      </Box>
                    );
                  })() : ''}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: '0.62rem', color: '#666', px: 0.4, py: 0.55, backgroundColor: poBg }}>
                  {poLine ? (
                    <InlineNumberEdit
                      value={Number(poLine.unitPrice.toFixed(2))}
                      onSave={(v) => onUpdatePoLineField(poLine.inventoryCode, 'unitPrice', v)}
                      min={0}
                      width={56}
                      align="right"
                      fontSize="0.62rem"
                      fontWeight={600}
                      tooltip="Edit purchase price (per unit)"
                      info={RECON_TABLE_INFO.poPurchasePrice}
                    />
                  ) : ''}
                </TableCell>
                {showSalesPrice && (
                  <TableCell align="right" sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#e65100', px: 0.8, py: 0.55, backgroundColor: idx % 2 === 0 ? '#fff8f0' : '#fff3e8' }}>
                    {poLine ? fmt(salesOf(poLine.unitPrice)) : ''}
                  </TableCell>
                )}
                <TableCell sx={{ p: 0, backgroundColor: '#6a3fc0' }} />

                {billLine ? (
                  <>
                    <TableCell sx={{ py: 0.4 }}><Checkbox size="small" checked={sel} onChange={() => onToggleSelect(billLine.id)} sx={{ p: 0 }} /></TableCell>
                    <TableCell sx={{ fontSize: '0.66rem', color: '#888' }}>{idx + 1}</TableCell>
                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', py: 0.4 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {billLine.codeMatch === 'partial' && billLine.lineType !== 'service' && supplier && (
                          <ImportResolutionBadge state={classifyImportLine({
                            supplierCode: billLine.supplierCode,
                            description: billLine.description,
                            supplierName: supplier.name,
                            products,
                            priceBookEntry: lookupSupplierPriceBookEntry(bill.supplierId, billLine.supplierCode),
                            remembered: lookupSupplierCode(bill.supplierId, billLine.supplierCode),
                          })} />
                        )}
                        <CodeCell
                          internalCode={billLine.internalCode}
                          supplierCode={trackSupplierCodes ? billLine.supplierCode : undefined}
                          lineType={billLine.lineType}
                          onResolve={() => onResolveBillLine(billLine.id)}
                          dense
                          allowReresolve
                          hideSupplier={!trackSupplierCodes}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: '0.68rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', '&:hover': { color: '#6a3fc0', textDecoration: 'underline dotted' } }}
                      onClick={(e) => setDescPopover({ anchor: e.currentTarget, text: billLine.description })}
                    >{billLine.description}</TableCell>
                    <TableCell align="center" sx={{ py: 0.4 }}>
                      <InlineNumberEdit
                        value={billLine.qty}
                        onSave={(v) => onUpdateBillLineQty(billLine.id, v)}
                        min={0}
                        width={46}
                        fontSize="0.7rem"
                        fontWeight={700}
                        colorFn={() => overBilled ? '#c62828' : '#333'}
                        tooltip={overBilled ? `Bill qty exceeds ordered (${poLine!.orderedQty}) — adjust qty` : 'Edit bill qty'}
                        info={RECON_TABLE_INFO.billQty}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.68rem' }}>{fmt(billLine.unitPrice)}</TableCell>
                    {showSalesPrice && (
                      <TableCell align="right" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#e65100', backgroundColor: '#fff8f0' }}>
                        {fmt(salesOf(billLine.unitPrice))}
                      </TableCell>
                    )}
                    <TableCell align="center"><GstBadge treatment={eg} isOverride={ov} onClick={() => onToggleLineGst(billLine.id, eg)} /></TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.66rem', color: '#888' }}>{fmt(billLine.gstAmount)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>
                      <Stack direction="row" spacing={0.4} alignItems="center" justifyContent="flex-end">
                        {billLine.copiedToJob && (
                          <InfoableButton info={RECON_TABLE_INFO.copiedToJobBill}>
                            <WorkIcon sx={{ fontSize: 11, color: '#2e7d32' }} />
                          </InfoableButton>
                        )}
                        <span>{fmt(billLine.lineTotal)}</span>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ py: 0.2 }}>
                      <InfoableButton info={RECON_TABLE_INFO.removeLine}>
                        <IconButton className="del-btn" size="small" onClick={() => onDeleteLine(billLine.id)}
                          sx={{ opacity: 0.2, transition: 'opacity 0.15s', color: '#c62828', p: 0.2, '&:hover': { opacity: 1, backgroundColor: '#fce4ec' } }}>
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </InfoableButton>
                    </TableCell>
                  </>
                ) : (
                  <TableCell
                    colSpan={showSalesPrice ? 11 : 10}
                    sx={{
                      backgroundColor: billPlaceholderBg, py: 0.7, px: 1.5,
                      fontStyle: 'italic', fontSize: '0.65rem',
                      color: isPoOnly ? '#e65100' : '#b0a0d8',
                      borderLeft: `2px solid ${isPoOnly ? '#ffe0b2' : '#d0c5f0'}`,
                    }}
                  >
                    {isPoOnly
                      ? '⚠ Not on this invoice — outstanding'
                      : '— Not yet imported'}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

    </Box>
  );
};
